import { JxsComponent } from '@jxs/component';

if (!customElements.get('jxs-marquee')) {
  class JxsMarquee extends JxsComponent {
    /** @type {string[]} */
    requiredRefs = ['viewport', 'track'];

    /** 当前位移（px，正数表示轨道整体左移） */
    #offset = 0;

    /** 「一组」的宽度：N 个 item + N 个 gap */
    #groupWidth = 0;

    /** 单个 item 的步长（item 宽 + gap），供 jump 使用 */
    #itemStep = 0;

    /** 每秒滚动 px 数 */
    #speed = 40;

    /** 1 向左，-1 向右 */
    #direction = 1;

    /** 上一帧的时间戳 */
    #lastTime = null;

    /** 交互后临时暂停到什么时刻（performance.now()） */
    #pauseUntil = 0;

    /** 鼠标悬停暂停 */
    #hoverPaused = false;

    /** 是否启用悬停暂停 */
    #hoverPauseEnabled = true;

    /** prefers-reduced-motion */
    #reduceMotion = false;

    /** 是否正在拖拽轨道 */
    #isDraggingTrack = false;

    /** 拖拽开始的 clientX */
    #dragStartX = 0;

    /** 拖拽开始的 offset */
    #dragStartOffset = 0;

    /** rAF id */
    #rafId = null;

    /** 组件是否在视口中可见 */
    #isVisible = false;

    /** 原始 item 数量 */
    #itemCount = 0;

    /** IntersectionObserver */
    #observer = null;

    /** ResizeObserver：track 尺寸变化（图片加载、断点切换）时重新测量 */
    #resizeObserver = null;

    /** resize handler */
    #onResize = () => {
      this.#measure();
    };

    /** visibilitychange handler */
    #onVisibilityChange = () => {
      if (document.hidden) {
        this.#stopAnimation();
      } else if (this.#isVisible) {
        this.#startAnimation();
      }
    };

    /** 是否已克隆过 */
    #cloned = false;

    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      this.#speed = /** @type {number} */ (this.getDataset('speed', 40));
      this.#direction = /** @type {number} */ (this.getDataset('direction', 1));
      this.#reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#cloneItems();
      this.#bindDrag();

      window.addEventListener('resize', this.#onResize);
      document.addEventListener('visibilitychange', this.#onVisibilityChange);

      // 字体加载完成后重新测量
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.#measure());
      }

      this.#measure();

      // ResizeObserver：图片加载完成 / 断点切换导致 item 尺寸变化时重新测量
      if ('ResizeObserver' in window && this.refs.track) {
        this.#resizeObserver = new ResizeObserver(() => {
          this.#measure();
        });
        this.#resizeObserver.observe(this.refs.track);
      }

      // IntersectionObserver：进入视口才启动 rAF
      if ('IntersectionObserver' in window) {
        this.#observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.#isVisible = entry.isIntersecting;
              if (entry.isIntersecting) {
                this.#measure();
                this.#startAnimation();
              } else {
                this.#stopAnimation();
              }
            });
          },
          { rootMargin: '120px 0px' }
        );
        this.#observer.observe(this);
      } else {
        this.#isVisible = true;
        this.#startAnimation();
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.#stopAnimation();
      this.#observer?.disconnect();
      this.#observer = null;
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
      window.removeEventListener('resize', this.#onResize);
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    }

    // ================================================================
    //  公开方法（供外部自定义 UI 调用）
    // ================================================================

    /**
     * 前进/后退一步。
     *
     * @param {number} dir - 1 前进，-1 后退
     */
    jump(dir) {
      if (this.#itemCount <= 1 || this.#groupWidth <= 0) return;
      this.#offset += dir * (this.#itemStep || this.#groupWidth / this.#itemCount);
      this.#offset = this.#wrap(this.#offset);
      this.#pauseUntil = performance.now() + 1500;
      this.#render();
    }

    /**
     * 跳到指定进度。
     *
     * @param {number} ratio - 0 ~ 1
     */
    seek(ratio) {
      if (this.#groupWidth <= 0) return;
      this.#offset = Math.max(0, Math.min(1, ratio)) * this.#groupWidth;
      this.#pauseUntil = performance.now() + 1500;
      this.#render();
    }

    /** 暂停自动滚动。 */
    pause() {
      this.#pauseUntil = Number.POSITIVE_INFINITY;
    }

    /** 恢复自动滚动。 */
    resume() {
      this.#pauseUntil = 0;
    }

    /**
     * 运行时修改速度。
     *
     * @param {number} pxPerSec - 每秒滚动多少 px
     */
    setSpeed(pxPerSec) {
      const value = Number(pxPerSec);
      this.#speed = Number.isFinite(value) ? value : 0;
    }

    /**
     * 运行时修改方向。
     *
     * @param {number} dir - 1 向左，-1 向右
     */
    setDirection(dir) {
      this.#direction = Number(dir) === -1 ? -1 : 1;
    }

    /**
     * 启用/禁用鼠标悬停暂停。
     *
     * @param {boolean} enabled
     */
    setHoverPause(enabled) {
      this.#hoverPauseEnabled = Boolean(enabled);
      if (!this.#hoverPauseEnabled) this.#hoverPaused = false;
    }

    /** 重新测量尺寸（响应式布局或内容变化时调用）。 */
    refresh() {
      this.#measure();
    }

    /** 当前进度比例（0 ~ 1）。 */
    get progress() {
      return this.#groupWidth > 0 ? this.#offset / this.#groupWidth : 0;
    }

    /** 当前速度（px/s）。 */
    get speed() {
      return this.#speed;
    }

    /** 当前方向（1 向左 / -1 向右）。 */
    get direction() {
      return this.#direction;
    }

    /** 是否正处于暂停状态。 */
    get paused() {
      return this.#hoverPaused || performance.now() < this.#pauseUntil;
    }

    // ================================================================
    //  私有实现
    // ================================================================

    /**
     * 克隆原始一组 item 追加到同一个 track 末尾。
     *
     * 克隆体加 aria-hidden，屏幕阅读器不会读两遍；
     * 同时移除 ref / id，避免污染 this.refs.items 和产生重复 id。
     */
    #cloneItems() {
      if (this.#cloned) return;

      const track = this.refs.track;
      if (!track) return;

      const originals = Array.from(track.children);
      this.#itemCount = originals.length;
      if (this.#itemCount < 1) return;

      originals.forEach((item) => {
        const clone = /** @type {Element} */ (item.cloneNode(true));

        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('ref');
        clone.removeAttribute('id');
        clone.querySelectorAll('[ref]').forEach((node) => node.removeAttribute('ref'));
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));

        track.appendChild(clone);
      });

      this.#cloned = true;
    }

    /**
     * 测量「一组」的宽度与单个 item 的步长。
     *
     * 用「第一个克隆体的 offsetLeft - 第一个原始体的 offsetLeft」，
     * 这个差值天然把 gap 算进去，且不受 track padding 影响。
     */
    #measure() {
      const track = this.refs.track;
      if (!track) return;

      const first = /** @type {HTMLElement | undefined} */ (track.children[0]);
      const firstClone = /** @type {HTMLElement | undefined} */ (track.children[this.#itemCount]);

      if (first && firstClone) {
        this.#groupWidth = firstClone.offsetLeft - first.offsetLeft;
      } else if (first) {
        this.#groupWidth = track.scrollWidth / 2;
      } else {
        this.#groupWidth = 0;
      }

      const second = /** @type {HTMLElement | undefined} */ (track.children[1]);

      if (second && first) {
        this.#itemStep = second.offsetLeft - first.offsetLeft;
      } else if (first) {
        this.#itemStep = first.offsetWidth;
      } else {
        this.#itemStep = 0;
      }

      if (!this.#itemStep && this.#groupWidth > 0) {
        this.#itemStep = this.#groupWidth / this.#itemCount;
      }

      if (this.#groupWidth > 0) {
        this.#offset = this.#wrap(this.#offset);
      }

      this.#render();
    }

    /**
     * 把任意 offset 规范到 [0, groupWidth)，负数也能正确回绕。
     *
     * @param {number} value
     * @returns {number}
     */
    #wrap(value) {
      if (this.#groupWidth <= 0) return 0;

      return ((value % this.#groupWidth) + this.#groupWidth) % this.#groupWidth;
    }

    /**
     * 唯一的「写 DOM」出口。
     *
     * 同时派发 `jxs-marquee:progress` 事件，外部自定义 UI（进度条等）可监听它同步状态。
     */
    #render() {
      const track = /** @type {HTMLElement | undefined} */ (this.refs.track);
      if (!track) return;

      track.style.transform = `translate3d(${-this.#offset}px, 0, 0)`;

      if (this.#groupWidth > 0) {
        this.dispatchEvent(
          new CustomEvent('jxs-marquee:progress', {
            detail: { ratio: this.#offset / this.#groupWidth, offset: this.#offset },
          })
        );
      }
    }

    /**
     * 自动滚动主循环，用真实帧间隔计算位移，速度与刷新率无关。
     *
     * @param {number} time
     */
    #tick = (time) => {
      if (!this.#isVisible) {
        this.#rafId = null;
        return;
      }

      if (this.#lastTime === null) this.#lastTime = time;

      const delta = time - this.#lastTime;
      this.#lastTime = time;

      if (!this.#reduceMotion && this.#groupWidth > 0 && time >= this.#pauseUntil && !this.#hoverPaused) {
        this.#offset += (this.#direction * this.#speed * delta) / 1000;

        // 无缝回绕：越过一组宽度就减掉一组，此时克隆组正好补位
        if (this.#offset >= this.#groupWidth) this.#offset -= this.#groupWidth;
        else if (this.#offset < 0) this.#offset += this.#groupWidth;

        this.#render();
      }

      this.#rafId = window.requestAnimationFrame(this.#tick);
    };

    #startAnimation() {
      if (this.#reduceMotion || this.#itemCount <= 1 || this.#rafId !== null) return;

      this.#lastTime = null;
      this.#rafId = window.requestAnimationFrame(this.#tick);
    }

    #stopAnimation() {
      this.#lastTime = null;

      if (this.#rafId !== null) {
        window.cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
      }
    }

    /** 绑定悬停暂停与轨道拖拽（Pointer Events）。 */
    #bindDrag() {
      const viewport = /** @type {HTMLElement | undefined} */ (this.refs.viewport);
      if (!viewport) return;

      viewport.addEventListener('mouseenter', () => {
        if (this.#hoverPauseEnabled) this.#hoverPaused = true;
      });

      viewport.addEventListener('mouseleave', () => {
        this.#hoverPaused = false;
      });

      // 不支持 Pointer Events 的浏览器优雅降级：只保留自动滚动
      if (!window.PointerEvent) return;

      viewport.addEventListener('pointerdown', (event) => {
        if (this.#itemCount <= 1 || this.#groupWidth <= 0) return;
        if (event.button !== undefined && event.button !== 0) return;

        this.#isDraggingTrack = true;
        this.#dragStartX = event.clientX;
        this.#dragStartOffset = this.#offset;
        this.#pauseUntil = Number.POSITIVE_INFINITY; // 拖拽期间彻底不自动走
        viewport.classList.add('is-dragging');

        if (viewport.setPointerCapture) viewport.setPointerCapture(event.pointerId);
      });

      viewport.addEventListener('pointermove', (event) => {
        if (!this.#isDraggingTrack || this.#groupWidth <= 0) return;

        this.#offset = this.#wrap(this.#dragStartOffset - (event.clientX - this.#dragStartX));
        this.#render();
        event.preventDefault();
      });

      /** @param {PointerEvent} [event] */
      const endDrag = (event) => {
        if (!this.#isDraggingTrack) return;

        this.#isDraggingTrack = false;
        this.#pauseUntil = performance.now() + 1500;
        viewport.classList.remove('is-dragging');

        if (event && viewport.releasePointerCapture && viewport.hasPointerCapture?.(event.pointerId)) {
          viewport.releasePointerCapture(event.pointerId);
        }
      };

      viewport.addEventListener('pointerup', endDrag);
      viewport.addEventListener('pointercancel', endDrag);
      viewport.addEventListener('lostpointercapture', () => endDrag());
    }
  }

  customElements.define('jxs-marquee', JxsMarquee);
}